/**
 * UsageTotalsGrid — плитки расхода: диалоги, ответы бота, токены (с долей
 * из кэша), голосовой помощник, платные и бесплатные сообщения WhatsApp и
 * смета по ставкам. Общая для раздела «Расход» и вкладки «Расход» карточки
 * клиента.
 */

import {paidMessages, type RateCard, type UsageTotals} from '@services/usage';
import {cn} from '@lib/utils';
import {formatCompact, formatNumber, formatTiyin} from '@utils/format';

type Props = {
  totals: UsageTotals;
  costTiyin: number;
  rateCard: RateCard;
};

export const UsageTotalsGrid = ({totals: t, costTiyin, rateCard}: Props) => {
  const noRates = Object.values(rateCard).every((v) => v === 0);
  const tiles: {label: string; value: string; sub: string; wide?: boolean}[] = [
    {
      label: 'Диалоги',
      value: formatNumber(t.dialogs),
      sub: `вызовов модели: ${formatNumber(t.modelCalls)}`,
    },
    {
      label: 'Ответы бота',
      value: formatNumber(t.assistantReplies),
      sub: 'отправлено, черновики, теневые',
    },
    {
      label: 'Токены ассистента',
      value: formatCompact(t.inputTokens + t.outputTokens),
      sub: `запрос ${formatCompact(t.inputTokens)} · ответ ${formatCompact(t.outputTokens)} · кэш ${formatCompact(t.cachedTokens)}`,
    },
    {
      label: 'Голосовой помощник',
      value: formatNumber(t.voiceCalls),
      sub: `обращений · токены: запрос ${formatCompact(t.voiceInputTokens)} · ответ ${formatCompact(t.voiceOutputTokens)}`,
    },
    {
      label: 'Платные WhatsApp',
      value: formatNumber(paidMessages(t)),
      sub: `напоминания ${t.waUtility} · маркетинг ${t.waMarketing} · коды ${t.waAuthentication}`,
    },
    {
      label: 'Бесплатные WhatsApp',
      value: formatNumber(t.waServiceFree),
      sub: 'ответы в открытое окно 24 ч',
    },
    {
      label: 'Смета',
      value: noRates ? '—' : formatTiyin(costTiyin),
      sub: noRates
        ? 'ставки в настройках бэка не заданы'
        : 'по прайс-листу из настроек, с голосовым помощником',
      // Итог — во всю ширину: шесть плиток выше ровно встают в ряды по 3 и по 2
      wide: true,
    },
  ];

  return (
    <div className="grid grid-cols-3 gap-2.5 max-md:grid-cols-2">
      {tiles.map((x) => (
        <div
          key={x.label}
          className={cn(
            'rounded-[12px] border border-skr-line bg-card p-[12px_14px]',
            x.wide === true && 'col-span-full',
          )}
        >
          <div className="text-[12px] text-muted-foreground">{x.label}</div>
          <div className="mt-1 font-mono text-[20px] font-semibold">{x.value}</div>
          <div className="mt-0.5 text-[11.5px] leading-[1.35] text-skr-faint">{x.sub}</div>
        </div>
      ))}
    </div>
  );
};
